import Vue from 'vue'
import Chart from 'chart.js'
import VueCharts from 'vue-chartjs'
import ChartStacked100 from 'chartjs-plugin-stacked100'
import ChartDataLabels from 'chartjs-plugin-datalabels'

import './chartjsRounded.js'
Chart.plugins.unregister(ChartDataLabels)

if (Chart.defaults.global.plugins.datalabels) {
  Chart.defaults.global.plugins.datalabels.display = false
} else {
  Chart.defaults.global.plugins.datalabels = { display: false }
}

Chart.defaults.global.defaultFontColor = '#8e8e8e'
Chart.defaults.global.defaultFontSize = 14
Chart.defaults.global.defaultFontFamily = 'ProximaNova Semibold'
// Para colocar todos ao mesmo tempo para quadrado
// try {
//   Chart.defaults.global.elements.point.pointStyle = 'rect'
// } catch (e) {
//   console.error(e)
// }
const { reactiveProp } = VueCharts.mixins

// Remove referencias circulares de um objeto antes de stringify.
// Tambem ignora a chave _meta (usada internamente pelo Chart.js e causa cycle).
const removeCircular = (obj) => {
  const seen = new WeakSet()
  return JSON.parse(
    JSON.stringify(obj, (key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return
        }
        seen.add(value)
      }
      return key === '_meta' ? undefined : value
    })
  )
}

// Factory para criar componentes Chart.js reativos a mudancas de
// chartData/options. Os 4 charts que precisam dessa reatividade
// (line, bar, bar-horizontal, pie) compartilham o mesmo bloco de
// codigo — ~50 linhas duplicadas — entao extraimos aqui.
//
// Charts simples sem watchers (donut, bubble, radar) nao usam essa
// factory por design: nesses casos o chartData nao muda em runtime.
const createReactiveChart = (BaseChart, plugins = []) => ({
  extends: BaseChart,
  plugins,
  mixins: [reactiveProp],
  props: {
    data: { type: Object, default: () => ({}) },
    options: { type: Object, default: () => ({}) },
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData, oldData) {
        if (
          JSON.stringify(removeCircular(newData)) !==
          JSON.stringify(removeCircular(oldData))
        ) {
          this.updateChart()
        }
      },
    },
    options: {
      deep: true,
      handler(newOptions, oldOptions) {
        if (
          JSON.stringify(removeCircular(newOptions)) !==
          JSON.stringify(removeCircular(oldOptions))
        ) {
          this.updateChart()
        }
      },
    },
  },
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
  methods: {
    updateChart() {
      if (this.$data._chart) {
        this.$data._chart.data = this.chartData
        this.$data._chart.options = this.options
        this.$data._chart.update()
      } else {
        this.renderChart(this.chartData, this.options)
      }
    },
  },
})

Chart.pluginService.register({
  beforeDraw: function (chart) {
    const {
      height,
      ctx,
      config: { data, type },
      chartArea: { top, bottom, left, right },
      options,
    } = chart

    if (type === 'doughnut' && chart.options.showTotal) {
      const total = data.datasets[0] ? data.datasets[0].total || 0 : 0

      const noCurrency = options && options.removeCurrencyTotalFor === true
      const currency = (options && options.totalCurrency) || 'BRL'

      const text = noCurrency
        ? total.toLocaleString('pt-BR')
        : total.toLocaleString('pt-BR', {
            style: 'currency',
            currency,
          })

      const oldFill = ctx.fillStyle
      const fontSize = ((height - top) / 300).toFixed(2)

      ctx.restore()
      ctx.font = `${fontSize}em ProximaNova Semibold`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'

      const centerX = left + (right - left) / 2
      const centerY = top + (bottom - top) / 2

      ctx.fillStyle = '#7E7E7E'
      ctx.font = `0.75rem ProximaNova Semibold`
      ctx.fillText('TOTAL', centerX, centerY - 8)

      ctx.fillStyle = '#5E5E5E'
      ctx.font = `${fontSize}em ProximaNova Semibold`
      ctx.fillText(text, centerX, centerY + 12)

      ctx.fillStyle = oldFill
      ctx.save()
    }
  },
})

// Traço vertical tracejado do marcador de aposentadoria (retirementLine).
const drawRetirementDash = (ctx, x, top, bottom, color) => {
  ctx.beginPath()
  ctx.setLineDash([5, 4])
  ctx.strokeStyle = color
  ctx.lineWidth = 1.5
  ctx.moveTo(x, top)
  ctx.lineTo(x, bottom)
  ctx.stroke()
}

// Label do marcador de aposentadoria (retirementLine), ao lado do traço.
const drawRetirementLabel = (ctx, x, top, color, label) => {
  ctx.setLineDash([])
  ctx.fillStyle = color
  ctx.font = '11px ProximaNova Semibold'
  ctx.textAlign = 'left'
  ctx.fillText(label, x + 6, top + 12)
}

// Posição X (px) do marcador de aposentadoria no eixo de categoria (labels
// em string) — getPixelForValue ignora o valor bruto nesse tipo de eixo,
// precisa do índice do label + getPixelForTick. Retorna null se a idade não
// estiver entre os labels do eixo. Exportada (só para teste, mesmo padrão de
// buildText em chartjs-plugins.js) — não é chamada de fora deste módulo.
export const retirementLineX = (chart, scale, age) => {
  const labelIndex = chart.data.labels.indexOf(String(age))
  return labelIndex === -1 ? null : scale.getPixelForTick(labelIndex)
}

// Linha vertical tracejada + label opcionais num line-chart (ex.: marcador
// de "aposentadoria" em fee-lab-evolucao-patrimonio.vue). Gated por
// chart.options.retirementLine, mesmo padrão do showTotal do donut acima —
// registro global porque createReactiveChart não aceita plugins por
// instância (o array de plugins fica fixo na definição do componente Vue).
Chart.pluginService.register({
  afterDraw: function (chart) {
    const retirementLine = chart.options && chart.options.retirementLine
    if (!retirementLine) return
    const scale = chart.scales && chart.scales['x-axis-0']
    if (!scale) return
    const x = retirementLineX(chart, scale, retirementLine.age)
    if (x === null) return
    const { top, bottom } = chart.chartArea
    const { ctx } = chart
    const color = retirementLine.color || '#8E8E8E'

    ctx.save()
    drawRetirementDash(ctx, x, top, bottom, color)
    if (retirementLine.label) {
      drawRetirementLabel(ctx, x, top, color, retirementLine.label)
    }
    ctx.restore()
  },
})

Vue.component('line-chart', createReactiveChart(VueCharts.Line, [ChartStacked100]))

Vue.component('donut-chart', {
  extends: VueCharts.Doughnut,
  mixins: [reactiveProp],
  props: {
    data: {
      type: Object,
      default: () => {
        return {}
      },
    },
    options: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
})

Vue.component('bar-chart', createReactiveChart(VueCharts.Bar, [ChartStacked100]))

Vue.component(
  'bar-horizontal-chart',
  createReactiveChart(VueCharts.HorizontalBar, [ChartStacked100])
)

Vue.component('bubble-chart', {
  extends: VueCharts.Bubble,
  mixins: [reactiveProp],
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Object,
      default: () => {},
    },
  },
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
})

Vue.component('pie-chart', createReactiveChart(VueCharts.Pie))

Vue.component('radar-chart', {
  extends: VueCharts.Radar,
  mixins: [reactiveProp],
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    options: {
      type: Object,
      default: () => {},
    },
  },
  mounted() {
    this.renderChart(this.chartData, this.options)
  },
})
